<script setup lang="ts">
import { selectOptionsListHappy } from '@/happyTheme/event/product/selectOptionsListHappy'
import { showSelectedModalProductHappy } from '@/happyTheme/event/product/showSelectedModalProductHappy'
import { initializeOptionIfNeeded } from '@/happyTheme/utils/product/button/initializeOptionIfNeeded'
import { decrementOptionValueHappy } from '@/happyTheme/utils/product/optionValue/decrementOptionValueHappy'
import { incrementOptionValueHappy } from '@/happyTheme/utils/product/optionValue/incrementOptionValueHappy'
import type { Option } from '@/storage/products'
import { computed, onMounted, ref, watch } from 'vue'
import { Plus, Minus } from 'lucide-vue-next'

const props = defineProps<{
    maxQuantity?: number
    currentQuantity: number
    option: Option
    currentOptionValueId: number
}>()

const counterOptionValue = ref(props.currentQuantity)

watch(
    () =>
        [
            selectOptionsListHappy.value[props.option.option_id]!,
            showSelectedModalProductHappy.value
        ] as const,
    ([newValue, newShow]) => {
        if (!newValue) return

        if (!selectOptionsListHappy.value[props.option.option_id]) return

        const optionValueFindReset = [
            ...selectOptionsListHappy.value[props.option.option_id].selected_values
                .checkbox_option.option_value_free,
            ...selectOptionsListHappy.value[props.option.option_id].selected_values
                .checkbox_option.option_value_paid
        ].filter((o) => o.option_value_reset === 1)

        const optionValueFind = props.option.values.find(
            (e) => e.option_value_id === props.currentOptionValueId
        )

        if (
            props.option.type === 'radio' &&
            selectOptionsListHappy.value[props.option.option_id].selected_values
                .radio_option.option_value_id !== props.currentOptionValueId &&
            selectOptionsListHappy.value[props.option.option_id].selected_values
                .radio_option.option_value_id !== null
        ) {
            counterOptionValue.value = 0
            return
        } else if (
            (props.option.type === 'select' || props.option.type === 'checkbox') &&
            optionValueFindReset.length !== 0
        ) {
            const hasReset = optionValueFindReset.some((element) => {
                counterOptionValue.value = 1
                return (
                    element.option_value_reset === 1 &&
                    element.option_value_id === optionValueFind?.option_value_id
                )
            })

            if (hasReset) {
                counterOptionValue.value = 1
                return
            }
        }

        counterOptionValue.value = props.currentQuantity
    },
    { deep: true, immediate: true }
)

const ShowButtonPlus = computed(() => {
    if (!props.option?.values || !Array.isArray(props.option.values)) {
        return false
    }

    const option = props.option
    const optionId = option.option_id
    const optionData = selectOptionsListHappy.value[optionId]

    if (!optionData?.selected_values) {
        return true
    }
    const type = option.type
    const currentOptionValue = option.values.find(
        (e) => e.option_value_id === props.currentOptionValueId
    )

    if (type === 'radio') {
        const selectedOptionsListRadio =
            selectOptionsListHappy.value[props.option.option_id]?.selected_values
                .radio_option
        if (selectedOptionsListRadio.option_value_id !== props.currentOptionValueId) {
            counterOptionValue.value = 0
            return true
        } else {
            return false
        }
    } else if (type === 'select') {
        const checkboxOptions =
            selectOptionsListHappy.value[props.option.option_id]?.selected_values
                ?.checkbox_option

        const allSelected = [
            ...checkboxOptions.option_value_free,
            ...checkboxOptions.option_value_paid
        ]

        const isThisOptionSelected = allSelected.some(
            (item) => item.option_value_id === props.currentOptionValueId
        )

        if (isThisOptionSelected) {
            return false
        }

        counterOptionValue.value = 0
        return true
    } else if (type === 'checkbox') {
        const selectedOptionListSelectFree =
            selectOptionsListHappy.value[props.option.option_id]?.selected_values
                .checkbox_option.option_value_free
        const selectedOptionListSelectPaid =
            selectOptionsListHappy.value[props.option.option_id]?.selected_values
                .checkbox_option.option_value_paid
        const findFreeReset = selectedOptionListSelectFree.find(
            (r) => r.option_value_id === props.currentOptionValueId
        )
        const findPaidReset = selectedOptionListSelectPaid.find(
            (r) => r.option_value_id === props.currentOptionValueId
        )

        if (currentOptionValue?.reset === 1 && !findFreeReset && !findPaidReset) {
            return true
        }

        if (selectOptionsListHappy.value[props.option.option_id].selected_values) {
            const selectedIds = [
                ...selectedOptionListSelectFree,
                ...selectedOptionListSelectPaid
            ]
            if (
                props.maxQuantity !== selectedIds.length &&
                currentOptionValue?.reset === 0
            ) {
                return true
            }
        }
    }
})

function useIncrementOptionValueHappy(optionValueId: number, optionId: number) {
    const maxQuantity = props.maxQuantity
    const option = props.option
    if (maxQuantity === undefined || maxQuantity === null) {
        return
    }
    if (!option || !option.values || option.values.length === 0) {
        return
    }

    incrementOptionValueHappy(
        optionValueId,
        optionId,
        maxQuantity,
        counterOptionValue,
        option
    )
}

function useDecrementOptionValueHappy(optionValueId: number, optionId: number) {
    const option = props.option
    if (!option || !option.values || option.values.length === 0) {
        return
    }

    decrementOptionValueHappy(optionValueId, optionId, counterOptionValue, option)
}

onMounted(() => {
    if (
        props.option &&
        selectOptionsListHappy.value[props.option.option_id] === undefined
    ) {
        initializeOptionIfNeeded(props.option)
    }
})
</script>

<template>
    <section class="section-button-select-option-value-happy">
        <button
            class=""
            :class="{
                'button-minus-option-value-happy': counterOptionValue > 0,
                hidden: counterOptionValue <= 0
            }"
            @click.stop="
                useDecrementOptionValueHappy(
                    props.currentOptionValueId,
                    props.option.option_id
                )
            "
        >
            <Minus :size="16" :stroke-width="2" class="svg-minus-happy" />
        </button>
        <p
            class=""
            :class="{
                'text-counter-option-value-happy': counterOptionValue > 0,
                hidden: counterOptionValue <= 0
            }"
        >
            {{ counterOptionValue }}
        </p>

        <button
            class="button-plus-option-value-happy"
            :class="{ 'button-plus-selected-happy': counterOptionValue > 0 }"
            @click.stop="
                useIncrementOptionValueHappy(
                    props.currentOptionValueId,
                    props.option.option_id
                )
            "
            v-if="ShowButtonPlus"
        >
            <Plus :size="16" :stroke-width="2" class="svg-plus-happy" />
        </button>
    </section>
</template>

<style scoped>
.section-button-select-option-value-happy {
    @apply min-w-24 flex items-center justify-end gap-2;
}

.button-minus-option-value-happy {
    /* refactored */
    @apply flex items-center border rounded p-1 shadow-sm;
    @apply bg-t-accent border-t-accent text-t-on-accent;
    @apply happy-ifo-dark:rounded-full happy-ifo-dark:p-1 happy-ifo-dark:transition-all happy-ifo-dark:duration-200;
    @apply happy-ifo-dark:border-t-accent/30 happy-ifo-dark:shadow-none happy-ifo-dark:bg-t-accent/10;
}

.svg-minus-happy {
    /* refactored: Lucide uses currentColor */
    @apply w-4 h-4 text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}

.text-counter-option-value-happy {
    /* refactored */
    @apply text-lg text-center align-baseline w-6 font-semibold text-t-text;
}

.button-plus-option-value-happy {
    /* refactored */
    @apply flex items-center p-1 shadow-sm rounded border;
    @apply bg-t-accent border-t-accent text-t-on-accent;
    @apply happy-ifo-dark:rounded-full happy-ifo-dark:p-1 happy-ifo-dark:transition-all happy-ifo-dark:duration-200;
    @apply happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:border-t-accent/30 happy-ifo-dark:shadow-none;
}

.svg-plus-happy {
    /* refactored: Lucide uses currentColor */
    @apply w-4 h-4 text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}
.hidden {
    display: none;
}

.button-plus-option-value-happy:active,
.button-plus-option-value-happy:focus {
    /* refactored */
    @apply bg-t-accent-hover border-t-accent-hover;
    @apply happy-ifo-dark:bg-t-accent-hover happy-ifo-dark:border-t-accent;
}

.button-plus-option-value-happy:active .svg-plus-happy,
.button-plus-option-value-happy:focus .svg-plus-happy {
    /* refactored: Lucide uses currentColor */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-text;
}

.button-minus-option-value-happy:active,
.button-minus-option-value-happy:focus {
    /* refactored */
    @apply bg-t-accent-hover border-t-accent-hover;
    @apply happy-ifo-dark:bg-t-accent-hover happy-ifo-dark:border-t-accent;
}
.button-minus-option-value-happy:active .svg-minus-happy,
.button-minus-option-value-happy:focus .svg-minus-happy {
    /* refactored: Lucide uses currentColor */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-text;
}

.button-plus-selected-happy {
    @apply happy-light:shadow-text-dark-happy-light;
    @apply happy-dark:shadow-text-dark-happy-light;
    @apply happy-urban-dark-red:shadow-urban-dark;
    @apply happy-urban-light-red:shadow-urban-dark;
    @apply happy-orange-light:shadow-urban-dark;
    @apply happy-orange-dark:shadow-urban-dark;
    @apply happy-fresh-mint:shadow-urban-dark;
    @apply happy-bee-light:shadow-bg-happy-light;
    @apply happy-bee-dark:shadow-urban-dark;
    @apply happy-ifo-dark:shadow-black/20;
    @apply happy-earth-stone:shadow-bg-happy-light;
    @apply happy-scorched-earth:bg-scorched-earth-yellow happy-scorched-earth:border-scorched-earth-yellow;
    @apply happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:shadow-urban-dark;
    @apply happy-purple-gradient:shadow-urban-dark;
}

.button-plus-selected-happy .svg-plus-happy {
    @apply happy-scorched-earth:text-urban-dark;
}

.button-plus-selected-happy:active,
.button-plus-selected-happy:focus {
    @apply happy-scorched-earth:bg-scorched-earth-red happy-scorched-earth:border-scorched-earth-yellow;
}

.button-plus-selected-happy:active .svg-plus-happy,
.button-plus-selected-happy:focus .svg-plus-happy {
    @apply happy-scorched-earth:text-bg-happy-light;
}

@media (hover: hover) {
    .button-plus-option-value-happy:hover {
        /* refactored */
        @apply bg-t-accent-hover border-t-accent-hover;
        @apply happy-ifo-dark:bg-t-accent-hover happy-ifo-dark:border-t-accent;
    }

    .button-plus-selected-happy:hover {
        @apply happy-scorched-earth:bg-scorched-earth-red happy-scorched-earth:border-scorched-earth-yellow;
    }
    .button-plus-selected-happy:hover .svg-plus-happy {
        @apply happy-scorched-earth:text-bg-happy-light;
    }

    .button-plus-option-value-happy:hover .svg-plus-happy {
        /* refactored: Lucide uses currentColor */
        @apply text-t-on-accent;
        @apply happy-ifo-dark:text-t-text;
    }
    .button-minus-option-value-happy:hover {
        /* refactored */
        @apply bg-t-accent-hover border-t-accent-hover;
        @apply happy-ifo-dark:bg-t-accent-hover happy-ifo-dark:border-t-accent;
    }
    .button-minus-option-value-happy:hover .svg-minus-happy {
        /* refactored: Lucide uses currentColor */
        @apply text-t-on-accent;
        @apply happy-ifo-dark:text-t-text;
    }
}
</style>
